import { getExactKeyPath, isLogicGroup } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { List } from 'immutable';
import { isOperator } from 'customer-data-filters-ui/filterQueryFormat/operator/Operator';
import { useContext } from 'react';
import { useValidateDraftCondition } from '../../hooks/useValidateDraftCondition';
import { useViewerActions } from '../../hooks/useViewerActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { WarningIcon } from 'foundations-assets';
import getConditionId from '../../utils/getConditionId';
import { isAnyEventTypeId } from '../../filterQueryFormat/ObjectTypeId';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getConditionErrorsAndRefinementErrorsCounts = condition => {
  const {
    errors,
    dateRefinement,
    numberRefinement
  } = condition;
  const {
    errors: dateRefinementErrors = []
  } = dateRefinement || {};
  const {
    errors: numberRefinementErrors = []
  } = numberRefinement || {};
  return {
    errorsCount: errors.length,
    dateRefinementErrorsCount: dateRefinementErrors.length,
    numberRefinementErrorsCount: numberRefinementErrors.length
  };
};
const getLogicGroupValidationErrorsCount = logicGroup => {
  if (!logicGroup) {
    return 0;
  }
  const logicGroupRefinementsErrorsCount = getLogicGroupValidationErrorsCount(logicGroup.refinements);
  const logicGroupErrorsCount = logicGroup.errors.length + logicGroupRefinementsErrorsCount;
  const conditions = logicGroup.conditions.valueSeq().toArray();
  const conditionsErrorsCount = conditions.reduce((count, condition) => {
    if (isOperator(condition)) {
      const {
        errorsCount,
        dateRefinementErrorsCount,
        numberRefinementErrorsCount
      } = getConditionErrorsAndRefinementErrorsCounts(condition);
      return count + errorsCount + dateRefinementErrorsCount + numberRefinementErrorsCount;
    }
    if (isLogicGroup(condition)) {
      return count + getLogicGroupValidationErrorsCount(condition);
    }
    return count;
  }, 0);
  return logicGroupErrorsCount + conditionsErrorsCount;
};
const hasLogicGroupRefinementsErrors = logicGroup => {
  if (!logicGroup.refinements) {
    return false;
  }
  const refinements = logicGroup.refinements.conditions.valueSeq().toArray();
  return logicGroup.refinements.errors.length !== 0 || refinements.some(refinement => {
    if (isOperator(refinement)) {
      return refinement.errors.length !== 0;
    }
    if (isLogicGroup(refinement)) {
      return hasLogicGroupRefinementsErrors(refinement);
    }
    return false;
  });
};
const getFirstError = (logicGroup, logicGroupPath = List.of()) => {
  if (logicGroup.errors.length || hasLogicGroupRefinementsErrors(logicGroup)) {
    return {
      conditionPath: logicGroupPath,
      filterFamily: logicGroup.filterFamily
    };
  }
  for (const [key, value] of logicGroup.conditions.entries()) {
    if (isLogicGroup(value)) {
      const maybeError = getFirstError(value, logicGroupPath.push(key));
      if (maybeError) {
        return maybeError;
      }
    }
    if (isOperator(value) && value.errors.length) {
      return {
        filterFamily: logicGroup.filterFamily,
        conditionPath: logicGroupPath.push(key)
      };
    }
    if (isOperator(value) && value.dateRefinement && isOperator(value.dateRefinement) && value.dateRefinement.errors.length) {
      return {
        filterFamily: logicGroup.filterFamily,
        conditionPath: logicGroupPath.push(key)
      };
    }
    if (isOperator(value) && value.numberRefinement && isOperator(value.numberRefinement) && value.numberRefinement.errors.length) {
      return {
        filterFamily: logicGroup.filterFamily,
        conditionPath: logicGroupPath.push(key)
      };
    }
  }
  return undefined;
};
const ErrorsCountBanner = () => {
  const {
    internalDraftValue
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    onEditFilter,
    onHighlightCondition
  } = useViewerActions();
  const {
    validateDraftCondition
  } = useValidateDraftCondition();
  const validationErrorsCount = getLogicGroupValidationErrorsCount(internalDraftValue);
  if (!validationErrorsCount) {
    return null;
  }
  const {
    conditionPath,
    filterFamily
  } = getFirstError(internalDraftValue);
  const onShowMeError = evt => {
    evt.stopPropagation();
    evt.nativeEvent.stopImmediatePropagation();
    const target = getExactKeyPath(conditionPath, internalDraftValue);
    const isUnifiedEvent = isAnyEventTypeId(filterFamily);
    if (isOperator(target) || isUnifiedEvent) {
      onEditFilter(conditionPath, filterFamily);
    }
    onHighlightCondition(getConditionId(conditionPath));
    validateDraftCondition();
  };
  return /*#__PURE__*/_jsx(Alert, {
    marginBlockStart: "300",
    type: "danger",
    titleText: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(WarningIcon, {
        color: "icon-alert-default"
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-selenium-info": validationErrorsCount,
          "data-selenium-test": "has-errors-msg",
          message: "newCustomerDataFilters.filtersViewer.allChangesIndicatorError.message",
          options: {
            count: validationErrorsCount
          }
        })
      }), /*#__PURE__*/_jsx(HelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.filtersViewer.allChangesIndicatorError.tooltip"
        }),
        tooltipPlacement: "bottom"
      })]
    }),
    children: /*#__PURE__*/_jsx(Button, {
      use: "link",
      "data-selenium-test": "scroll-to-error-btn",
      onClick: onShowMeError,
      children: /*#__PURE__*/_jsx("span", {
        style: {
          textDecoration: 'underline'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.filtersViewer.allChangesIndicatorError.showMe"
        })
      })
    })
  });
};
export default ErrorsCountBanner;